

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Box,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    IconButton,
    Snackbar,
    Alert,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";

const NewDayLineItemModal = ({
    isOpen,
    onClose,
    onSave,
}) => {
    const { axiosInstance } = useAuth();
    const [isSaving, setIsSaving] = useState(false);
    const [formData, setFormData] = useState({
        name: '', // Auto-generated usually, but we might ask for it or generate backend
        itinerary_day_id: '',
        type: '',
        mandatory: true,
        start: '',
        end: '',
        geo_location: '',
        min_pax: '',
        max_pax: '',
        notes: '',
        trip_component_id: '',
        sort_order: '',
        title: ''
    });

    const [itineraryDays, setItineraryDays] = useState([]);
    const [tripComponents, setTripComponents] = useState([]);
    const [loadingData, setLoadingData] = useState(false);
    const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

    useEffect(() => {
        if (isOpen) {
            fetchItineraryDays();
            fetchTripComponents();
            // Reset form when opening
            setFormData({
                name: '', itinerary_day_id: '', type: '', mandatory: true,
                start: '', end: '', geo_location: '', min_pax: '',
                max_pax: '', notes: '', trip_component_id: '', sort_order: '', title: ''
            });
        }
    }, [isOpen]);

    const fetchItineraryDays = async () => {
        setLoadingData(true);
        try {
            const response = await axiosInstance.get('/itinerary-days/');
            setItineraryDays(response.data);
        } catch (error) {
            console.error('Error fetching itinerary days:', error);
        } finally {
            setLoadingData(false);
        }
    };

    const fetchTripComponents = async () => {
        try {
            const response = await axiosInstance.get('/trip-components/');
            setTripComponents(response.data || []);
        } catch (error) {
            console.error('Error fetching Trip Logistics:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSaving(true);
        try {
            const payload = {
                ...formData,
                itinerary_day_id: parseInt(formData.itinerary_day_id),
                trip_component_id: formData.trip_component_id && formData.trip_component_id !== '' ? parseInt(formData.trip_component_id) : null,
                min_pax: formData.min_pax ? parseInt(formData.min_pax) : null,
                max_pax: formData.max_pax ? parseInt(formData.max_pax) : null,
                sort_order: formData.sort_order ? parseInt(formData.sort_order) : null,
                start: formData.start ? new Date(formData.start).toISOString() : null,
                end: formData.end ? new Date(formData.end).toISOString() : null,
                // If name is left blank, backend generates it
                name: formData.name || undefined
            };

            await onSave(payload);
            onClose();
        } catch (error) {
            console.error('Error saving Day Line Item:', error);
            let errorMessage = 'Error saving Day Line Item';
            
            if (error.response?.data) {
                if (typeof error.response.data.detail === 'string') {
                    errorMessage = error.response.data.detail;
                } else if (Array.isArray(error.response.data.detail)) {
                    errorMessage = error.response.data.detail.map(err => 
                        typeof err === 'string' ? err : err.msg || JSON.stringify(err)
                    ).join(', ');
                } else if (typeof error.response.data.detail === 'object') {
                    errorMessage = error.response.data.detail.msg || JSON.stringify(error.response.data.detail);
                }
            }
            
            setToast({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setIsSaving(false);
        }
    };

    const typeOptions = ['Accommodation', 'Meal', 'Activity', 'Transfer', 'Misc'];

    return (
        <>
            <Modal open={isOpen} onClose={onClose}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '95%', sm: '80%', md: '800px' },
                    maxHeight: '90vh',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box className="pearl-popup-header" sx={{
                        backgroundColor: colors.popupHeader.background,
                        px: 3,
                        py: 2,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <MDTypography variant="h6" color="white">Add New Day Line Item</MDTypography>
                        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ p: 4, overflowY: 'auto' }}>
                        {loadingData ? (
                            <Grid container spacing={2} justifyContent="center">
                                <Grid item>
                                    <CircularProgress />
                                </Grid>
                            </Grid>
                        ) : (
                            <form onSubmit={handleSubmit}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Name (Auto-generated if empty)"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" required>
                                            <InputLabel>Itinerary Day</InputLabel>
                                            <Select
                                                name="itinerary_day_id"
                                                value={formData.itinerary_day_id}
                                                onChange={handleInputChange}
                                                label="Itinerary Day"

                                            >
                                                <MenuItem value=""><em>Select Itinerary Day</em></MenuItem>
                                                {itineraryDays.map(day => (
                                                    <MenuItem key={day.id} value={day.id}>{day.name} - {day.title}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal" required>
                                            <InputLabel>Type</InputLabel>
                                            <Select
                                                name="type"
                                                value={formData.type}
                                                onChange={handleInputChange}
                                                label="Type"

                                            >
                                                <MenuItem value=""><em>Select Type</em></MenuItem>
                                                {typeOptions.map(opt => (
                                                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Title"
                                            name="title"
                                            value={formData.title}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Start Time"
                                            name="start"
                                            type="datetime-local"
                                            value={formData.start}
                                            onChange={handleInputChange}
                                            InputLabelProps={{ shrink: true }}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="End Time"
                                            name="end"
                                            type="datetime-local"
                                            value={formData.end}
                                            onChange={handleInputChange}
                                            InputLabelProps={{ shrink: true }}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Geo Location"
                                            name="geo_location"
                                            value={formData.geo_location}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Min Pax"
                                            name="min_pax"
                                            value={formData.min_pax}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Max Pax"
                                            name="max_pax"
                                            value={formData.max_pax}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            type="number"
                                            label="Sort Order"
                                            name="sort_order"
                                            value={formData.sort_order}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth variant="outlined" margin="normal">
                                            <InputLabel>Trip Logistics</InputLabel>
                                            <Select
                                                name="trip_component_id"
                                                value={formData.trip_component_id}
                                                onChange={handleInputChange}
                                                label="Trip Logistics"

                                            >
                                                <MenuItem value=""><em>None</em></MenuItem>
                                                {tripComponents.map(component => (
                                                    <MenuItem key={component.id} value={component.id}>
                                                        {component.name || `Trip Logistics #${component.id}`}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <MDBox mt={2}>
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        name="mandatory"
                                                        checked={formData.mandatory}
                                                        onChange={handleInputChange}
                                                    />
                                                }
                                                label="Mandatory"
                                            />
                                        </MDBox>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Notes"
                                            name="notes"
                                            multiline
                                            rows={3}
                                            value={formData.notes}
                                            onChange={handleInputChange}
                                            variant="outlined"
                                            margin="normal"
                                        />
                                    </Grid>
                                </Grid>

                                <MDBox display="flex" justifyContent="flex-end" mt={4} gap={1}>
                                    <MDButton variant="outlined" color="info" onClick={onClose}>
                                        Cancel
                                    </MDButton>
                                    <MDButton
                                        variant="gradient"
                                        color="info"
                                        type="submit"
                                        disabled={isSaving}
                                    >
                                        {isSaving ? <CircularProgress size={20} color="white" /> : "Save"}
                                    </MDButton>
                                </MDBox>
                            </form>
                        )}
                    </Box>
                </Box>
            </Modal>

            <Snackbar
                open={toast.open}
                autoHideDuration={6000}
                onClose={() => setToast({ ...toast, open: false })}
            >
                <Alert severity={toast.severity} onClose={() => setToast({ ...toast, open: false })}>
                    {toast.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default NewDayLineItemModal;
